import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionHead } from '@/features/identity';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Field, Skeleton, useToastStore } from '@/shared/ui';

import {
  useCreateElementType,
  useDeleteElementType,
  useElementTypes,
  useRenameElementType,
  useSetElementTypeActive,
} from '../queries/useElement';
import type { ElementType } from '../model/types';

import styles from './ElementTypesSection.module.css';

/**
 * Słownik typów elementu (decyzja 83).
 *
 * ⭐ Sekcja istnieje, bo typ **przestał być ozdobą**: selektor kampanii wybiera po nim
 * i to **dokładną równością**. Przy wolnym tekście „Serwis" i „serwis" to dwa różne zbiory,
 * więc licznik kampanii pokazywałby „34/120 zrobione" i **milczał o brakujących piętnastu**.
 *
 * ⚠ Sekcja mieszka w `identity`, bo to **sekcja ustawień** — powłoka, nagłówek i style są tutaj.
 * Dane bierze z `elements`, bo typ należy do grafu. Odwrotne ułożenie zmuszałoby `elements`
 * do importu arkusza stylów ustawień, czyli do wiedzy o cudzym ekranie.
 */
export function ElementTypesSection() {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  // Ustawienia chcą też wycofanych — inaczej nie dałoby się ich przywrócić.
  const types = useElementTypes(false);
  const create = useCreateElementType();

  const [name, setName] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();

    const wanted = name.trim();

    if (wanted.length === 0) {
      return;
    }

    create.mutate(wanted, {
      onSuccess: () => {
        setName('');
        show({ message: t('settings.elementTypes.added', { name: wanted }), tone: 'success' });
      },
      onError: (error) => show({ message: describeError(error), tone: 'danger' }),
    });
  }

  return (
    <>
      <SectionHead
        titleKey="settings.elementTypes.title"
        leadKey="settings.elementTypes.lead"
      />

      <form className={styles['inlineForm']} onSubmit={submit}>
        <Field
          label={t('settings.elementTypes.newLabel')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={100}
        />
        <Button type="submit" variant="primary" disabled={create.isPending}>
          {t('settings.elementTypes.add')}
        </Button>
      </form>

      <section>
        {types.isPending ? <Skeleton /> : null}

        {types.data?.length === 0 ? (
          <p className={styles['lead']}>{t('settings.elementTypes.empty')}</p>
        ) : null}

        {types.data && types.data.length > 0 ? (
          <table className={styles['table']}>
            <thead>
              <tr>
                <th>{t('settings.elementTypes.column.name')}</th>
                <th>{t('settings.elementTypes.column.state')}</th>
                <th>{t('settings.elementTypes.column.elements')}</th>
                <th>{t('settings.elementTypes.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {types.data.map((type) => (
                <TypeRow key={type.id} type={type} />
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </>
  );
}

function TypeRow({ type }: { type: ElementType }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);

  const rename = useRenameElementType();
  const setActive = useSetElementTypeActive();
  const remove = useDeleteElementType();

  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(type.name);
  // Usunięcie jest nieodwracalne, więc idzie w dwóch krokach — ten sam wzorzec co odnowienie
  // sekretnego linku (decyzja 78). Odmowa „typ jest używany" i tak przyjdzie z serwera.
  const [confirming, setConfirming] = useState(false);

  function submitRename(event: FormEvent) {
    event.preventDefault();

    const wanted = name.trim();

    if (wanted.length === 0 || wanted === type.name) {
      setRenaming(false);
      return;
    }

    rename.mutate(
      { id: type.id, name: wanted },
      {
        onSuccess: (renamed) => {
          setRenaming(false);
          // ⚠ Komunikat mówi o elementach, bo zmiana nazwy typu je PRZEPISUJE — człowiek
          // ma wiedzieć, że dotknął czegoś więcej niż jednej pozycji słownika.
          show({
            message: t('settings.elementTypes.renamed', {
              name: wanted,
              count: renamed.elementCount,
            }),
            tone: 'success',
          });
        },
        onError: (error) => show({ message: describeError(error), tone: 'danger' }),
      },
    );
  }

  return (
    <>
      <tr>
        <td className={styles['name']}>{type.name}</td>

        <td>
          <span
            className={`${styles['badge']} ${type.isActive ? styles['badgeOff'] : styles['badgePending']}`}
          >
            {t(type.isActive ? 'settings.elementTypes.state.active' : 'settings.elementTypes.state.retired')}
          </span>
        </td>

        <td className={styles['login']}>{type.elementCount}</td>

        <td className={styles['actions']}>
          <Button variant="ghost" onClick={() => setRenaming((open) => !open)}>
            {t('settings.elementTypes.action.rename')}
          </Button>

          <Button
            variant="ghost"
            disabled={setActive.isPending}
            onClick={() =>
              setActive.mutate(
                { id: type.id, isActive: !type.isActive },
                { onError: (error) => show({ message: describeError(error), tone: 'danger' }) },
              )
            }
          >
            {t(type.isActive ? 'settings.elementTypes.action.retire' : 'settings.elementTypes.action.restore')}
          </Button>

          <Button
            variant="ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (!confirming) {
                setConfirming(true);
                return;
              }

              remove.mutate(type.id, {
                onSuccess: () =>
                  show({
                    message: t('settings.elementTypes.deleted', { name: type.name }),
                    tone: 'success',
                  }),
                // ⛔ „Typ jest używany" przychodzi stąd i jest NORMALNYM wynikiem, nie awarią:
                // front nie powiela reguły, bo licznik elementów jest w tabeli obok.
                onError: (error) => {
                  setConfirming(false);
                  show({ message: describeError(error), tone: 'danger' });
                },
              });
            }}
          >
            {t(confirming ? 'settings.elementTypes.action.deleteConfirm' : 'settings.elementTypes.action.delete')}
          </Button>
        </td>
      </tr>

      {renaming ? (
        <tr>
          <td colSpan={4}>
            <form className={styles['inlineForm']} onSubmit={submitRename}>
              <Field
                label={t('settings.elementTypes.renameLabel', { name: type.name })}
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={100}
              />
              <Button type="submit" variant="primary" disabled={rename.isPending}>
                {t('settings.elementTypes.action.saveName')}
              </Button>
            </form>
          </td>
        </tr>
      ) : null}
    </>
  );
}
